import { PrimeIndemnite, StatusType } from '../types';
import { PRORATED_PRIMES, PRIME_CALCULATION } from '../constants/primes';
import { SALARY_CALCULATION } from '../constants/salary';

/**
 * @fileoverview Prime calculation utilities for French public hospital system
 * 
 * This module provides comprehensive calculation functions for various primes and allowances
 * available to healthcare professionals in the French public hospital system (Fonction Publique Hospitalière).
 * 
 * All calculations are based on official French government regulations and use the current
 * indice majoré system and point values as of 2024.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

/**
 * Calculates the gross monthly treatment (traitement indiciaire brut mensuel) based on the indice majoré
 * 
 * This is the fundamental calculation for all French public service salaries using
 * the official point value directly.
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré) from the official grid
 * @returns {number} The gross monthly treatment in euros
 * 
 * @example
 * // Calculate monthly treatment for IM 472 (typical nurse level)
 * const monthlySalary = calculateTraitementIndiciaireMensuel(472);
 * console.log(monthlySalary); // 2323.55 (472 × 4.92278)
 * 
 * @example
 * // Direct formula for IM 472:
 * // 472 × 4.92278 € = 2,323.55 €
 * 
 * @see {@link https://www.fonction-publique.gouv.fr/valeur-du-point-dindice} Official point value
 */
export function calculateTraitementIndiciaireMensuel(indiceMajoré: number): number {
  return indiceMajoré * SALARY_CALCULATION.POINT_VALUE;
}

/**
 * Calculates the gross annual treatment (traitement indiciaire brut annuel) based on the indice majoré
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @returns {number} The gross annual treatment in euros
 * 
 * @example
 * // Calculate annual treatment for IM 472
 * const annualTreatment = calculateTraitementIndiciaireAnnuel(472);
 * console.log(annualTreatment); // 27,883.54 (472 × 4.92278 × 12)
 */
export function calculateTraitementIndiciaireAnnuel(indiceMajoré: number): number {
  return indiceMajoré * SALARY_CALCULATION.POINT_VALUE * SALARY_CALCULATION.MONTHS_PER_YEAR;
}

/**
 * Calculates the Prime Spéciale de Sujétion (PSS) - Special Hardship Allowance
 * 
 * The PSS compensates for specific job difficulties such as:
 * - Physical demands and working conditions
 * - Irregular schedules and shift work
 * - Professional risks and responsibilities
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @returns {number} The monthly PSS amount in euros (rounded to nearest euro)
 * 
 * @example
 * // Calculate PSS for IM 472
 * const pss = calculatePSSAmount(472);
 * console.log(pss); // 232 (10% of monthly salary)
 * 
 * @example
 * // Formula breakdown:
 * // Monthly salary = 472 × 5,907.34 € / 1,200 = 2,323.55 €
 * // PSS = 2,323.55 € × 10% = 232.36 € (rounded to 232 €)
 * 
 * @see {@link https://www.fonction-publique.gouv.fr/etre-agent-public/ma-remuneration/le-regime-indemnitaire-fixe/la-prime-speciale-de-sujetion-pss} Official PSS documentation
 */
export function calculatePSSAmount(indiceMajoré: number): number {
  const traitementMensuel = calculateTraitementIndiciaireMensuel(indiceMajoré);
  return Math.round(traitementMensuel * PRIME_CALCULATION.PSS_TAUX);
}

/**
 * Calculates the Indemnité de Sujétion Spéciale (ISS) - "13 Hours Allowance"
 * 
 * The ISS is commonly known as the "prime des 13 heures" and compensates nurses for:
 * - Working irregular hours and overtime
 * - On-call duties and emergency availability
 * - Additional responsibilities beyond standard working hours
 * 
 * The calculation uses a fixed ratio of 13/1900 applied to the annual gross treatment
 * calculated with the 2021 point value (4,6860€), not the current point value.
 * Formula: (Indice majoré historique × 4,6860€ × 12) × 13/1900
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @returns {number} The monthly ISS amount in euros (rounded to nearest cent)
 * 
 * @example
 * // Calculate ISS for IM 472
 * const iss = calculateISSAmount(472);
 * console.log(iss); // 189.52 (based on 13/1900 of annual treatment with 2021 point value)
 * 
 * @example
 * // Formula breakdown:
 * // Annual treatment 2021 = 472 × 4,6860€ × 12 = 26,508.86€
 * // ISS monthly = 26,508.86€ × 13/1,900 = 181.33€
 * 
 * @see {@link https://www.emploi-collectivites.fr/iss-treize-heures-indemnite-blog-territorial} ISS information
 */
export function calculateISSAmount(indiceMajoré: number): number {
  // Use 2021 point value for ISS calculation
  const traitementAnnuel = indiceMajoré * SALARY_CALCULATION.POINT_VALUE_2021 * SALARY_CALCULATION.MONTHS_PER_YEAR;
  const issAnnuel = (traitementAnnuel * PRIME_CALCULATION.ISS_NUMERATEUR) / PRIME_CALCULATION.ISS_DENOMINATEUR;
  return Math.round(issAnnuel * 100) / 100; // Round to nearest cent
}

/**
 * Calculates the Supplément Familial de Traitement (SFT) - Family Allowance Supplement
 * 
 * The SFT is a mandatory component of public service remuneration for employees with
 * at least one dependent child under 20 years old. It consists of:
 * - Fixed part: Based on number of children
 * - Proportional part: Percentage of salary (from 2 children onwards)
 * 
 * The calculation uses a capped indice majoré between 454 and 722 for the proportional part.
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @param {number} nombreEnfants - Number of dependent children under 20
 * @returns {number} The monthly SFT amount in euros (rounded to nearest cent)
 * 
 * @example
 * // Calculate SFT for IM 472 with 2 children
 * const sft = calculateSFTAmount(472, 2);
 * console.log(sft); // 80.37 (10.67€ fixed + 69.70€ proportional)
 * 
 * @example
 * // SFT breakdown for 3 children:
 * // Fixed part: 15.24€
 * // Proportional part: 8% of capped salary
 * // Capped IM = min(722, max(454, 472)) = 472
 * // Monthly salary = 472 × 5,907.34€ / 1,200 = 2,323.55€
 * // Proportional = 2,323.55€ × 8% = 185.88€
 * // Total SFT = 15.24€ + 185.88€ = 201.12€
 * 
 * @see {@link https://outils.cisirh.gouv.fr/calculatrice-sft/form} Official SFT calculator
 */
export function calculateSFTAmount(indiceMajoré: number, nombreEnfants: number): number {
  if (nombreEnfants === 0) return 0;
  
  // Part fixe selon le nombre d'enfants
  let partFixe = 0;
  if (nombreEnfants === 1) {
    partFixe = PRIME_CALCULATION.SFT_PARTS_FIXES[1];
  } else if (nombreEnfants === 2) {
    partFixe = PRIME_CALCULATION.SFT_PARTS_FIXES[2];
  } else if (nombreEnfants === 3) {
    partFixe = PRIME_CALCULATION.SFT_PARTS_FIXES[3];
  } else {
    // 3 enfants + enfants supplémentaires
    const enfantsSupplementaires = nombreEnfants - 3;
    partFixe = PRIME_CALCULATION.SFT_PARTS_FIXES[3] + 
               (enfantsSupplementaires * PRIME_CALCULATION.SFT_PARTS_FIXES.ENFANT_SUPPLEMENTAIRE);
  }
  
  // Part proportionnelle (seulement à partir de 2 enfants)
  let partProportionnelle = 0;
  if (nombreEnfants >= 2) {
    // Appliquer le plafonnement de l'indice majoré
    const indicePlafonne = Math.min(
      PRIME_CALCULATION.SFT_IM_PLAFOND,
      Math.max(PRIME_CALCULATION.SFT_IM_PLANCHER, indiceMajoré)
    );
    
    const traitementMensuel = calculateTraitementIndiciaireMensuel(indicePlafonne);
    
    let tauxProportionnel = 0;
    if (nombreEnfants === 2) {
      tauxProportionnel = PRIME_CALCULATION.SFT_PARTS_PROPORTIONNELLES[2];
    } else if (nombreEnfants === 3) {
      tauxProportionnel = PRIME_CALCULATION.SFT_PARTS_PROPORTIONNELLES[3];
    } else {
      // 3 enfants + enfants supplémentaires
      const enfantsSupplementaires = nombreEnfants - 3;
      tauxProportionnel = PRIME_CALCULATION.SFT_PARTS_PROPORTIONNELLES[3] + 
                         (enfantsSupplementaires * PRIME_CALCULATION.SFT_PARTS_PROPORTIONNELLES.ENFANT_SUPPLEMENTAIRE);
    }
    
    partProportionnelle = traitementMensuel * tauxProportionnel;
  }
  
  return Math.round((partFixe + partProportionnelle) * 100) / 100;
}

/**
 * Calculates the Indemnité de Résidence (IR) - Residence Allowance
 * 
 * The IR compensates for higher living costs in certain geographical areas.
 * France is divided into 3 zones with different allowance rates:
 * - Zone 1: 3% (Paris and surrounding communes)
 * - Zone 2: 1% (Major metropolitan areas)
 * - Zone 3: 0% (Other communes)
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @param {1|2|3} zone - The geographical zone (1, 2, or 3)
 * @returns {number} The monthly IR amount in euros (rounded to nearest euro)
 * 
 * @example
 * // Calculate IR for IM 472 in Zone 1 (Paris)
 * const ir = calculateIRAmount(472, 1);
 * console.log(ir); // 70 (3% of annual salary / 12)
 * 
 * @example
 * // IR calculation breakdown for Zone 1:
 * // Annual salary = 472 × 59.07€ = 27,881€
 * // Zone 1 rate = 3%
 * // Annual IR = 27,881€ × 3% = 836€
 * // Monthly IR = 836€ / 12 = 70€
 * 
 * @see {@link https://www.service-public.fr/particuliers/vosdroits/F32511} Official IR zones
 */
export function calculateIRAmount(indiceMajoré: number, zone: 1 | 2 | 3): number {
  const traitementAnnuel = calculateTraitementIndiciaireAnnuel(indiceMajoré);
  
  let taux = 0;
  switch (zone) {
    case 1:
      taux = PRIME_CALCULATION.IR_ZONE_1_TAUX;
      break;
    case 2:
      taux = PRIME_CALCULATION.IR_ZONE_2_TAUX;
      break;
    case 3:
      taux = PRIME_CALCULATION.IR_ZONE_3_TAUX;
      break;
  }
  
  return Math.round((traitementAnnuel * taux) / 12); // Montant mensuel
}

/**
 * Calculates the Prime de nuit - Night Shift Allowance
 * 
 * The night shift allowance provides a 25% salary increase for hours worked during
 * night shifts. The calculation is prorated based on the number of nights worked
 * in the month relative to a 30-day month.
 * 
 * This allowance has been applicable since January 1, 2024.
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @param {number} nuitsParMois - Number of nights worked per month (0-31)
 * @returns {number} The monthly night shift allowance in euros (rounded to nearest euro)
 * 
 * @example
 * // Calculate night allowance for IM 472 with 10 nights per month
 * const primeNuit = calculatePrimeNuitAmount(472, 10);
 * console.log(primeNuit); // 194 (25% increase prorated for 10/30 nights)
 * 
 * @example
 * // Night allowance calculation breakdown:
 * // Monthly salary = 472 × 4.92278€ = 2,323.55€
 * // Night increase = 2,323.55€ × 25% = 580.89€
 * // Prorated = 580.89€ × (10/30) = 193.63€ (rounded to 194€)
 * 
 * @see {@link https://sante.gouv.fr/fichiers/bo/2023/2023.22.sante.pdf} Official night shift regulations
 */
export function calculatePrimeNuitAmount(indiceMajoré: number, nuitsParMois: number): number {
  const salaireMensuel = calculateTraitementIndiciaireMensuel(indiceMajoré);
  const majoration = salaireMensuel * PRIME_CALCULATION.PRIME_NUIT_MAJORATION;
  const prorata = nuitsParMois / 30; // Prorata sur 30 jours
  
  return Math.round(majoration * prorata);
}

/**
 * Calculates the Complément de Traitement Indiciaire (CTI) amount
 * 
 * The CTI is calculated based on a fixed number of indice majoré points
 * multiplied by the current point value. This system ensures that the
 * allowance follows the evolution of the point value in the public service.
 * 
 * @param {number} ctiPoints - Number of CTI points (e.g., 49 for Ségur CTI)
 * @returns {number} The monthly CTI amount in euros (rounded to nearest cent)
 * 
 * @example
 * // Calculate Ségur CTI with 49 points
 * const ctiSegur = calculateCTIAmount(49);
 * console.log(ctiSegur); // 241.22 (49 × 4.92278€)
 * 
 * @example
 * // CTI calculation breakdown:
 * // CTI points = 49
 * // Point value = 4.92278€ (2024 value)
 * // CTI amount = 49 × 4.92278€ = 241.22€/month
 */
export function calculateCTIAmount(ctiPoints: number): number {
  return Math.round((ctiPoints * SALARY_CALCULATION.POINT_VALUE) * 100) / 100;
}

/**
 * Calculates a percentage of the gross annual treatment (traitement indiciaire brut)
 * 
 * This function calculates a monthly amount based on a percentage of the annual
 * gross treatment (traitement indiciaire brut annuel). It's used for primes that
 * are defined as a percentage of the base salary.
 * 
 * @param {number} indiceMajoré - The salary index (indice majoré)
 * @param {number} percentage - The percentage to apply (e.g., 17 for 17%)
 * @returns {number} The monthly amount in euros (rounded to nearest cent)
 * 
 * @example
 * // Calculate 17% of annual treatment for IM 472
 * const amount = calculatePercentageOfTreatment(472, 17);
 * console.log(amount); // 395.01€ (17% of annual treatment / 12)
 * 
 * @example
 * // Formula breakdown for IM 472 with 17%:
 * // Annual treatment = 472 × 5,907.34€ = 27,882.64€
 * // 17% of annual = 27,882.64€ × 17% = 4,740.05€
 * // Monthly amount = 4,740.05€ / 12 = 395.01€
 */
export function calculatePercentageOfTreatment(indiceMajoré: number, percentage: number): number {
  const traitementAnnuel = calculateTraitementIndiciaireAnnuel(indiceMajoré);
  const montantAnnuel = traitementAnnuel * (percentage / 100);
  return Math.round((montantAnnuel / 12) * 100) / 100;
}

/**
 * Calculates the adjusted prime amount based on work time percentage
 * 
 * Some primes are prorated according to the employee's work time percentage.
 * This function applies the prorating only to eligible primes defined in PRORATED_PRIMES.
 * 
 * @param {PrimeIndemnite} prime - The prime object containing amount and configuration
 * @param {number} workTimePercentage - Work time percentage (0-100)
 * @returns {number} The adjusted prime amount in euros
 * 
 * @example
 * // Calculate adjusted Ségur prime for 80% work time
 * const segurPrime = { id: 'segur', montantUtilisateur: 238, ... };
 * const adjusted = calculateAdjustedPrimeAmount(segurPrime, 80);
 * console.log(adjusted); // 190.4 (238€ × 80%)
 * 
 * @example
 * // Non-prorated prime remains unchanged
 * const transportPrime = { id: 'transport', montantUtilisateur: 30, ... };
 * const adjusted = calculateAdjustedPrimeAmount(transportPrime, 80);
 * console.log(adjusted); // 30 (unchanged)
 */
export function calculateAdjustedPrimeAmount(
  prime: PrimeIndemnite, 
  workTimePercentage: number
): number {
  if (PRORATED_PRIMES.includes(prime.id as typeof PRORATED_PRIMES[number])) {
    return prime.montantUtilisateur * (workTimePercentage / 100);
  }
  return prime.montantUtilisateur;
}

/**
 * Calculates the total monthly primes amount for all active primes
 * 
 * This function sums all active primes, applying work time adjustments where applicable.
 * Only primes marked as active are included in the calculation.
 * 
 * @param {PrimeIndemnite[]} primes - Array of all available primes
 * @param {number} workTimePercentage - Work time percentage (0-100)
 * @returns {number} The total monthly primes amount in euros
 * 
 * @example
 * // Calculate total for active primes with 100% work time
 * const total = calculateTotalMonthlyPrimes(allPrimes, 100);
 * console.log(total); // 450.50
 * 
 * @example
 * // Calculate total for active primes with 80% work time
 * const total = calculateTotalMonthlyPrimes(allPrimes, 80);
 * console.log(total); // 380.40 (some primes prorated)
 */
export function calculateTotalMonthlyPrimes(
  primes: PrimeIndemnite[], 
  workTimePercentage: number
): number {
  return primes
    .filter(prime => prime.active)
    .reduce((total, prime) => total + calculateAdjustedPrimeAmount(prime, workTimePercentage), 0);
}

/**
 * Calculates the Nouvelle Bonification Indiciaire (NBI) amount
 * 
 * The NBI is calculated by multiplying the number of NBI points by the current
 * point value in the public service. NBI points range from 15 to 120 depending
 * on the specific functions, responsibilities, and technical requirements of the position.
 * 
 * @param {number} points - Number of NBI points (15-120)
 * @returns {number} The monthly NBI amount in euros (rounded to nearest cent)
 * 
 * @example
 * // Calculate NBI for 30 points
 * const nbi = calculateNBIAmount(30);
 * console.log(nbi); // 147.68 (30 × 4.92278€)
 * 
 * @example
 * // NBI calculation breakdown:
 * // NBI points = 30
 * // Point value = 4.92278€ (2024 value)
 * // NBI amount = 30 × 4.92278€ = 147.68€/month
 */
export function calculateNBIAmount(points: number): number {
  return Math.round((points * SALARY_CALCULATION.POINT_VALUE) * 100) / 100;
}

/**
 * Calculates the reimbursement amount for expense-based primes
 * 
 * Some primes are calculated as a percentage reimbursement of actual expenses
 * (e.g., transport costs). This function calculates the reimbursement amount.
 * 
 * @param {number} baseAmount - The base expense amount in euros
 * @param {number} reimbursementRate - The reimbursement rate as a percentage (0-100)
 * @returns {number} The reimbursement amount in euros
 * 
 * @example
 * // Calculate transport reimbursement (75% of 40€ monthly pass)
 * const reimbursement = calculateReimbursementAmount(40, 75);
 * console.log(reimbursement); // 30
 * 
 * @example
 * // Calculate meal voucher reimbursement (50% of 150€ monthly)
 * const reimbursement = calculateReimbursementAmount(150, 50);
 * console.log(reimbursement); // 75
 */
export function calculateReimbursementAmount(
  baseAmount: number, 
  reimbursementRate: number
): number {
  return (baseAmount * reimbursementRate) / 100;
}

/**
 * Calculates the Prime d'insalubrité Catégorie 2 amount
 * 
 * This allowance compensates for exposure to category 2 unsanitary conditions.
 * The calculation is based on the number of working days per month and a fixed
 * amount per half-day of exposure.
 * 
 * @param {number} montantDemiJournee - Amount per half-day of exposure in euros
 * @param {number} joursTravaillesParMois - Number of working days per month
 * @returns {number} The monthly insalubrité cat 2 amount in euros
 * 
 * @example
 * // Calculate insalubrité cat 2 for 22 working days with 0.31€ per half-day
 * const insalubrite = calculateInsalubriteCat2Amount(0.31, 22);
 * console.log(insalubrite); // 13.64 (0.31€ × 2 × 22 days)
 */
export function calculateInsalubriteCat2Amount(montantDemiJournee: number, joursTravaillesParMois: number): number {
  return Math.round((montantDemiJournee * 2 * joursTravaillesParMois) * 100) / 100;
}

/**
 * Validates a prime amount input value
 * 
 * Ensures that prime amounts are within acceptable ranges and are valid numbers.
 * Used for form validation and data integrity checks.
 * 
 * @param {number} amount - The amount to validate in euros
 * @returns {boolean} True if the amount is valid, false otherwise
 * 
 * @example
 * console.log(validatePrimeAmount(250)); // true
 * console.log(validatePrimeAmount(-10)); // false (negative)
 * console.log(validatePrimeAmount(15000)); // false (too high)
 * console.log(validatePrimeAmount(NaN)); // false (not a number)
 */
export function validatePrimeAmount(amount: number): boolean {
  return amount >= 0 && amount <= 10000 && Number.isFinite(amount);
}

/**
 * Validates a reimbursement rate input value
 * 
 * Ensures that reimbursement rates are within the valid percentage range (0-100%).
 * 
 * @param {number} rate - The rate to validate as a percentage (0-100)
 * @returns {boolean} True if the rate is valid, false otherwise
 * 
 * @example
 * console.log(validateReimbursementRate(75)); // true
 * console.log(validateReimbursementRate(-5)); // false (negative)
 * console.log(validateReimbursementRate(150)); // false (over 100%)
 * console.log(validateReimbursementRate(NaN)); // false (not a number)
 */
export function validateReimbursementRate(rate: number): boolean {
  return rate >= 0 && rate <= 100 && Number.isFinite(rate);
}

/**
 * Filters primes based on eligible statuses for the selected status
 * 
 * Some primes are only available for specific employment statuses (titulaire, stagiaire, contractuel).
 * This function filters primes based on the eligibleStatuses property.
 * 
 * @param {PrimeIndemnite[]} primes - Array of primes to filter
 * @param {StatusType} selectedStatus - Currently selected employment status
 * @returns {PrimeIndemnite[]} Array of primes available for the selected status
 * 
 * @example
 * // Filter primes for titulaire status
 * const titulairesPrimes = filterPrimesByStatus(allPrimes, 'titulaire');
 * 
 * @example
 * // Filter primes for contractuel status (may exclude some primes)
 * const contractuelPrimes = filterPrimesByStatus(allPrimes, 'contractuel');
 */
export function filterPrimesByStatus(
  primes: PrimeIndemnite[], 
  selectedStatus: StatusType
): PrimeIndemnite[] {
  return primes.filter(prime => {
    if (!prime.eligibleStatuses || prime.eligibleStatuses.length === 0) {
      return true;
    }
    return prime.eligibleStatuses.includes(selectedStatus);
  });
}

/**
 * Filters primes available for a specific profession
 * 
 * Different healthcare professions have access to different primes and allowances.
 * This function filters the complete list of primes to show only those available
 * for the specified profession.
 * 
 * @param {PrimeIndemnite[]} primes - Array of all available primes
 * @param {string} professionId - The profession identifier (e.g., 'infirmier', 'medecin')
 * @returns {PrimeIndemnite[]} Array of primes available for the profession
 * 
 * @example
 * // Get primes available for nurses
 * const nursePrimes = filterPrimesForProfession(allPrimes, 'infirmier');
 * console.log(nursePrimes.length); // 12 (nurse-specific primes)
 * 
 * @example
 * // Get primes available for doctors
 * const doctorPrimes = filterPrimesForProfession(allPrimes, 'medecin');
 * console.log(doctorPrimes.length); // 8 (doctor-specific primes)
 */
export function filterPrimesForProfession(
  primes: PrimeIndemnite[], 
  professionId: string
): PrimeIndemnite[] {
  return primes.filter(prime => prime.availableForProfessions.includes(professionId));
}

/**
 * Filters primes based on eligible echelons for the selected echelon
 * 
 * Some primes are only available for specific echelons (e.g., début de carrière
 * is only for echelons 1 and 2). This function filters primes based on the
 * eligibleEchelons property.
 * 
 * @param {PrimeIndemnite[]} primes - Array of primes to filter
 * @param {number} selectedEchelon - Currently selected echelon (0 if none selected)
 * @returns {PrimeIndemnite[]} Array of primes available for the selected echelon
 * 
 * @example
 * // Filter primes for echelon 1 (should include début de carrière)
 * const echelon1Primes = filterPrimesByEchelon(allPrimes, 1);
 * 
 * @example
 * // Filter primes for echelon 5 (should exclude début de carrière)
 * const echelon5Primes = filterPrimesByEchelon(allPrimes, 5);
 */
export function filterPrimesByEchelon(
  primes: PrimeIndemnite[], 
  selectedEchelon: number
): PrimeIndemnite[] {
  if (selectedEchelon === 0) {
    return primes; // No echelon selected, show all primes
  }
  
  return primes.filter(prime => {
    // If eligibleEchelons is null or undefined, prime is available for all echelons
    if (!prime.eligibleEchelons || prime.eligibleEchelons.length === 0) {
      return true;
    }
    
    // Check if selected echelon is in the eligible echelons list
    return prime.eligibleEchelons.includes(selectedEchelon);
  });
}